import type { ComponentType, FC, ReactNode } from 'react'
import type { ToolInfo } from '@/constants/tools'

import dynamic from 'next/dynamic'
import { ArrowRight, Lock } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'

const ToolLoading: FC = (): ReactNode => (
    <div className='flex min-h-64 items-center justify-center text-sm text-slate-500'>Loading the tool.</div>
)

const TOOL_COMPONENTS: Record<string, ComponentType> = {
    'compress-image': dynamic(() => import('@/components/tools/compress-tool'), { loading: ToolLoading }),
    'resize-image': dynamic(() => import('@/components/tools/resize-tool'), { loading: ToolLoading }),
    'convert-image': dynamic(() => import('@/components/tools/convert-tool'), { loading: ToolLoading }),
    'heic-to-jpg': dynamic(() => import('@/components/tools/heic-tool'), { loading: ToolLoading }),
    'crop-image': dynamic(() => import('@/components/tools/crop-tool'), { loading: ToolLoading }),
    'images-to-pdf': dynamic(() => import('@/components/tools/pdf-tool'), { loading: ToolLoading }),
    'remove-image-metadata': dynamic(() => import('@/components/tools/metadata-tool'), { loading: ToolLoading }),
    'batch-resize-images': dynamic(() => import('@/components/tools/batch-resize-tool'), { loading: ToolLoading }),
    'aspect-ratio': dynamic(() => import('@/components/tools/aspect-ratio-tool'), { loading: ToolLoading })
}

interface ToolPageProps {
    tool: ToolInfo
}

const ToolPage: FC<ToolPageProps> = ({ tool }): ReactNode => {
    const Tool = TOOL_COMPONENTS[tool.slug]

    return (
        <Layout>
            <Seo title={tool.seoTitle} description={tool.description} />

            <div className='min-h-screen bg-[#060b14] text-slate-200'>
                <SiteHeader />

                <section id='hero' className='relative overflow-hidden'>
                    <div className='pointer-events-none absolute -top-40 left-1/2 h-[420px] w-[900px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl' />
                    <div className='relative mx-auto max-w-6xl px-6 pt-14 pb-10 md:pt-20'>
                        <nav aria-label='Breadcrumb' className='text-sm text-slate-500'>
                            <a href='/tools' className='hover:text-cyan-300'>Free tools</a>
                            <span className='mx-2'>/</span>
                            <span className='text-slate-300'>{tool.name}</span>
                        </nav>
                        <p className='mt-6 font-mono text-xs tracking-[0.2em] text-cyan-300 uppercase'>Free browser tool</p>
                        <h1 className='mt-4 max-w-3xl text-3xl leading-tight font-extrabold tracking-tight text-white md:text-5xl'>{tool.heading}</h1>
                        <p className='mt-5 max-w-2xl text-lg leading-relaxed text-slate-400'>{tool.intro}</p>
                        <p className='mt-4 inline-flex items-center gap-2 text-sm text-slate-400'>
                            <Lock className='size-4 text-cyan-300' /> No account. Your files stay on your device.
                        </p>
                    </div>
                </section>

                <section id='tool' className='mx-auto max-w-6xl px-6 pb-16'>
                    <div className='rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-8'>
                        {Tool ? <Tool /> : null}
                    </div>
                </section>

                <section id='about-tool' className='border-y border-white/10 bg-[#081120]'>
                    <div className='mx-auto max-w-6xl px-6 py-16'>
                        <h2 className='text-2xl font-extrabold text-white'>Good to know</h2>
                        <div className='mt-8 grid gap-6 md:grid-cols-3'>
                            {tool.notes.map((note) => (
                                <article key={note.heading} className='border-t border-white/10 pt-5'>
                                    <h3 className='font-bold text-white'>{note.heading}</h3>
                                    <p className='mt-2 text-sm leading-relaxed text-slate-400'>{note.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section id='related' className='mx-auto max-w-6xl px-6 py-16'>
                    <h2 className='text-2xl font-extrabold text-white'>Keep the file moving</h2>
                    <ul className='mt-6 flex flex-wrap gap-3'>
                        {tool.related.map((link) => (
                            <li key={link.href}>
                                <a href={link.href} className='inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-200 transition hover:border-cyan-300/50'>
                                    {link.label} <ArrowRight className='size-4 text-cyan-300' />
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-12 flex flex-col items-start justify-between gap-5 rounded-3xl border border-cyan-300/15 bg-gradient-to-b from-[#0f1d33] to-[#0a1424] p-6 sm:p-8 md:flex-row md:items-center'>
                        <div>
                            <h2 className='text-lg font-bold text-white'>Need to change the text in a PDF next?</h2>
                            <p className='mt-1 text-sm text-slate-400'>DOCSCRUB edits existing PDF text, signs documents and removes hidden details, on your device.</p>
                        </div>
                        <a href='/docscrub' className='inline-flex shrink-0 items-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200'>
                            Meet DOCSCRUB <ArrowRight className='size-4' />
                        </a>
                    </div>
                </section>

                <SiteFooter />
            </div>
        </Layout>
    )
}

export default ToolPage
